import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent, type ReactNode } from "react";

import {
  CASE_GONE_SENTENCE,
  CASE_KEY,
  CASES_KEY,
  caseGone,
  ProblemError,
  RECORD_KEY,
  setCaseStatus,
  type CaseRecordOut,
  type CaseStatus,
} from "@/api";
import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { TextArea } from "@/components/fields/TextArea";
import { StateMessage } from "@/components/StateMessage";

import styles from "./Record.module.css";

/**
 * The two moves that carry a reason (spec 2026-10-15 §5), told apart by
 * their words and by one rule: a cancellation insists on the reason, a
 * restoration only asks for it. Every sentence of both dialogs is in this
 * table — the code below has none of its own.
 *
 * `whenRefused` is the 409. The status route answers it with the table's own
 * sentence („Von „cancelled“ nach „cancelled“ führt kein Übergang."), which
 * names two status keys and says nothing a person can act on. What happened
 * is told from the status the record shows once it was read again: the
 * second tab got there first, or the Vorgang moved on to somewhere else.
 */
const REASON_ID = "status-move-reason";

const WORDS = {
  cancel: {
    to: "cancelled" as CaseStatus,
    heading: "Vorgang stornieren",
    reasonLabel: "Grund (Pflicht)",
    blankHint: "Ohne Grund bleibt „Stornieren“ gesperrt.",
    submit: "Stornieren",
    working: "Wird storniert …",
    done: "Vorgang storniert.",
    unreachable: "Nicht storniert — Server nicht erreichbar.",
    refused: (now: CaseStatus | undefined) =>
      now === "cancelled"
        ? "Der Vorgang ist schon storniert. Die Akte zeigt, von wem und warum."
        : "Der Vorgang hat sich inzwischen geändert und lässt sich nicht mehr stornieren. Die Akte zeigt den aktuellen Stand.",
  },
  restore: {
    to: "planning_in_progress" as CaseStatus,
    heading: "Vorgang wiederherstellen",
    reasonLabel: "Grund (optional)",
    blankHint: null,
    submit: "Wiederherstellen",
    working: "Wird wiederhergestellt …",
    done: "Vorgang wiederhergestellt.",
    unreachable: "Nicht wiederhergestellt — Server nicht erreichbar.",
    refused: (now: CaseStatus | undefined) =>
      now !== undefined && now !== "cancelled"
        ? "Der Vorgang ist schon wiederhergestellt. Die Akte zeigt den aktuellen Stand."
        : "Der Vorgang hat sich inzwischen geändert und lässt sich nicht mehr wiederherstellen. Die Akte zeigt den aktuellen Stand.",
  },
} as const;

export type StatusMoveKind = keyof typeof WORDS;

interface StatusMoveDialogProps {
  kind: StatusMoveKind;
  caseId: string;
  caseNo: string;
  title: string;
  /** What the move means, in the sentences above the reason. */
  children: ReactNode;
  onClose: () => void;
  /** The move went through and the record has been read again. */
  onDone: (sentence: string) => void;
  /** Once the dialog is gone and the focus is back on its opener (`Dialog.onClosed`). */
  onClosed?: () => void;
}

/**
 * „Vorgang stornieren …" and „Wiederherstellen" in one dialog (spec
 * 2026-10-15 §5). Nothing is deleted either way; the reason goes onto the
 * history row (`case_status_history.reason`) and is read back as the
 * record's banner.
 *
 * The button is held while a required reason is blank — `aria-disabled`, not
 * `disabled`, so it stays reachable and its reason is read out with it (the
 * hint under the field, the button's `aria-describedby`). While the request
 * is out it keeps the focus and says what it does; Escape does not close
 * (`busy` on the `Dialog`), and a second click is swallowed by the button
 * *and* by `sending` — the state would only turn in the next render.
 *
 * Refusals keep the dialog open, and the person's text with it:
 *
 * - **409** — a second tab or a second click got there first, or the Vorgang
 *   moved on (`WORDS.refused`). The record is read again; nothing is left to
 *   ask, so the button is held and the way out says „Schließen".
 * - **404** — handed over meanwhile; the record turns into „nicht gefunden".
 * - anything else — the server's sentence, or that it could not be reached;
 *   the button is free to try again.
 */
export function StatusMoveDialog({ kind, caseId, caseNo, title, children, onClose, onDone, onClosed }: StatusMoveDialogProps) {
  const words = WORDS[kind];
  const required = kind === "cancel";
  const client = useQueryClient();
  const submitRef = useRef<HTMLButtonElement>(null);
  const sending = useRef(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  // Nothing left to ask: the Vorgang is not where this move starts from.
  const [over, setOver] = useState(false);

  // A NUL byte is not whitespace to `trim()` and Postgres refuses it in
  // `text` — removed as the server does, so what is sent is what is kept.
  const why = reason.split("\u0000").join("").trim();
  const blank = required && why === "";
  const held = blank || over;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sending.current || held) return;
    sending.current = true;
    setBusy(true);
    setRefused(null);
    try {
      await setCaseStatus(caseId, words.to, why === "" ? undefined : why);
      void client.invalidateQueries({ queryKey: CASES_KEY });
      // The record behind the dialog first: it closes on the Vorgang as it
      // now stands, and the button that opened it may be gone by then.
      await client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      onDone(words.done);
    } catch (cause) {
      if (caseGone(cause)) {
        setOver(true);
        setRefused(CASE_GONE_SENTENCE);
        void client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
      } else if (cause instanceof ProblemError && cause.status === 409) {
        // The button stays busy until the record is read again: the sentence
        // is told from what it shows, and a click now would meet the same 409.
        void client.invalidateQueries({ queryKey: CASES_KEY });
        await client.invalidateQueries({ queryKey: CASE_KEY(caseId) });
        setOver(true);
        setRefused(words.refused(client.getQueryData<CaseRecordOut>(RECORD_KEY(caseId))?.case.status));
      } else {
        setRefused(cause instanceof ProblemError ? cause.userMessage : words.unreachable);
      }
      sending.current = false;
      setBusy(false);
      // No field is wrong — the focus goes back to the button.
      submitRef.current?.focus();
    }
  }

  return (
    <Dialog
      title={words.heading}
      subtitle={
        <>
          <span className="num">{caseNo}</span> · {title}
        </>
      }
      onClose={onClose}
      onClosed={onClosed}
      busy={busy}
    >
      <form className={styles.dialogForm} noValidate onSubmit={(event) => void submit(event)}>
        {children}
        <TextArea
          id={REASON_ID}
          label={words.reasonLabel}
          value={reason}
          onChange={setReason}
          rows={3}
          maxLength={500}
          readOnly={over}
          hint={blank ? words.blankHint : null}
        />
        {/* The server's refusal in the one notice pattern — red edge, symbol
            and word, the only `role="alert"` here. */}
        {refused !== null ? <StateMessage id="status-move-refused" tone="conflict" live="alert" title={refused} /> : null}
        <DialogFooter>
          <Button onClick={onClose} aria-disabled={busy ? "true" : undefined}>
            {over ? "Schließen" : "Abbrechen"}
          </Button>
          <Button
            ref={submitRef}
            type="submit"
            variant="primary"
            busy={busy}
            aria-disabled={busy || held ? "true" : undefined}
            aria-describedby={over ? "status-move-refused" : blank && words.blankHint !== null ? `${REASON_ID}-hint` : undefined}
          >
            {busy ? words.working : words.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
